/**
 * In-memory cache of the last hotel listing for a search key so returning from
 * a hotel detail page can show the same results instantly without refetching.
 */

const MAX_KEYS = 8;
const store = new Map();

/** Last listing snapshot (same key as in-memory) — survives component remount if tab stays open. */
const SESSION_LAST_LISTING = 'tourasco_hotels_last_listing_v1';

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  return window.sessionStorage;
}

function readListingSnapshotFromSession(fetchKey) {
  const ss = getSessionStorage();
  if (!ss || !fetchKey) return null;
  try {
    const raw = ss.getItem(SESSION_LAST_LISTING);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.key !== fetchKey || !parsed.snapshot) return null;
    const snap = parsed.snapshot;
    if (!Array.isArray(snap.hotels)) return null;
    return snap;
  } catch {
    return null;
  }
}

/** Stable key for listing cache — must match between merge and get (trimmed fields). */
export function buildHotelListingFetchKey(searchParams) {
  const sp = searchParams && typeof searchParams === 'object' ? searchParams : {};
  const st = (sp.searchTarget ?? 'free').toString().trim().toLowerCase();
  return JSON.stringify({
    cityId: (sp.cityId ?? '').toString().trim(),
    propertyId: (sp.propertyId ?? '').toString().trim(),
    location: (sp.location ?? '').toString().trim(),
    searchTarget: st || 'free',
  });
}

export function listingCacheHasHotelPrices(cached) {
  if (!cached || typeof cached.hotelPrices !== 'object' || cached.hotelPrices === null) {
    return false;
  }
  return Object.keys(cached.hotelPrices).length > 0;
}

export function getHotelListingCache(key) {
  if (!key) return null;
  const mem = store.get(key);
  if (mem != null) return mem;
  return readListingSnapshotFromSession(key);
}

export function mergeHotelListingCache(key, partial) {
  if (!key) return;
  const prev = store.get(key) || {};
  const next = { ...prev, ...partial };
  store.set(key, next);
  if (store.size > MAX_KEYS) {
    const firstKey = store.keys().next().value;
    store.delete(firstKey);
  }
  if (Array.isArray(next.hotels)) {
    const ss = getSessionStorage();
    if (ss) {
      try {
        ss.setItem(SESSION_LAST_LISTING, JSON.stringify({ key, snapshot: next }));
      } catch {
        /* quota / private mode */
      }
    }
  }
}
